/** 13.10 (владелец: «в учебных играх … кто из сотрудников будет играть … отчёты друг о друге»): на главной
 *  кабинета — учебные игры, где человек ИГРОК (на бронь не назначен: ни денег, ни отчёта об игре), и отчёты
 *  друг о друге, которые ОО запросил у него не через отчёт игры (игрок или оператор с другой игры филиала).
 *  День — как у «Игр дня»: свои игры без ночи + ночной хвост D+1. Писать можно с 30 минут до начала игры. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { addDays, fmtDateApi, isNightTail } from "../../utils/date";
import { PeerOnlySheet, type PeerGame } from "./PeerOnlySheet";
import s from "./sidePlayer.module.css";

type Css = Readonly<Record<string, string>>;
type Game = { booking_id: number; date: string; time: string; quest: string; branch: string; learning: boolean };
type Req = Game & { subject_id: string | null; subject_name: string; done: boolean; activity: string; impression: string; open: boolean };
type Mine = { requests?: Req[]; playing?: Game[] };

const OPEN_BEFORE_MIN = 30;
const startMs = (g: Game) => Date.parse(`${g.date}T${g.time}:00+05:00`);
/** «откроется в 18:30» — начало игры минус 30 минут, как считает сервер (peer-mine.ts). */
const opensAt = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  const t = ((h ?? 0) * 60 + (m ?? 0) - OPEN_BEFORE_MIN + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export function SidePlayer({ date, css }: { date: Date; css: Css }) {
  const [data, setData] = useState<{ req: Req[]; play: Game[] }>({ req: [], play: [] });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  const [sheet, setSheet] = useState<number | null>(null); /* booking_id открытой шторки */

  useEffect(() => {
    const ac = new AbortController();
    const get = (d: Date) => api<Mine>(`/api/peer-review/mine?date=${fmtDateApi(d)}`, { signal: ac.signal }).catch(() => ({}) as Mine);
    void Promise.all([get(date), get(addDays(date, 1))]).then(([day, next]) => {
      if (ac.signal.aborted) return;
      /* 27.08-правило смены: ночные игры D — вчерашней смены, ночной хвост D+1 — этой */
      const shift = <T extends Game>(a: T[] = [], b: T[] = []) => [...a.filter((r) => !isNightTail(r.time)), ...b.filter((r) => isNightTail(r.time))];
      setData({ req: shift(day.requests, next.requests), play: shift(day.playing, next.playing) });
    });
    return () => ac.abort();
  }, [date, tick]);

  /* Отчёт открывается сам: перечитать к «за 30 минут до начала» ближайшей закрытой игры
     (не чаще раза в минуту — если часы телефона спешат, сервер ещё скажет «закрыто»;
     не дальше 6 ч — таймер браузера на недели вперёд срабатывает сразу). */
  useEffect(() => {
    const waits = data.req.filter((r) => !r.open).map((r) => startMs(r) - OPEN_BEFORE_MIN * 60_000 - Date.now()).filter((ms) => !Number.isNaN(ms));
    if (!waits.length) return;
    const t = window.setTimeout(reload, Math.min(Math.max(Math.min(...waits), 55_000), 6 * 3_600_000) + 5_000);
    return () => window.clearTimeout(t);
  }, [data.req, reload]);

  /* запросы — карточкой на игру: о ком писать и что с каждым */
  const games = useMemo(() => {
    const by = new Map<number, PeerGame>();
    for (const r of data.req) {
      const g = by.get(r.booking_id) ?? { bookingId: r.booking_id, quest: r.quest, time: r.time, branch: r.branch, learning: r.learning, open: r.open, subjects: [] };
      g.subjects.push({ id: r.subject_id, name: r.subject_name, done: r.done, activity: r.activity, impression: r.impression });
      by.set(r.booking_id, g);
    }
    return [...by.values()];
  }, [data.req]);

  if (!data.req.length && !data.play.length) return null;
  const doneCnt = data.req.filter((r) => r.done).length;
  const close = () => { setSheet(null); reload(); }; /* и после отмены: часть могла уйти до ошибки */
  return (
    <>
      {data.play.length ? (
        <>
          <div className={`${css.secHead} ${css.studyHead}`}>
            <span className={css.ico}>🎮</span> Ты играешь <span className={css.cnt}>({data.play.length})</span>
          </div>
          {data.play.map((p) => (
            <div key={`play-${p.booking_id}`} className={`${css.dopCard} ${css.studyCard}`}>
              <div className={css.dopHeadRow}>
                <span className={css.dopTime}>{p.time}</span>
                {p.branch ? <span className={css.dopBranch}>{p.branch}</span> : null}
              </div>
              <div className={css.dopDesc}>{p.quest}</div>
              <div className={css.dopHint}>{p.learning ? "учебная игра · " : ""}ты в команде игроков — без оплаты и отчёта об игре</div>
            </div>
          ))}
        </>
      ) : null}
      {games.length ? (
        <>
          <div className={`${css.secHead} ${s.peerHead}`}>
            <span className={css.ico}>✍</span> Отчёты друг о друге <span className={css.cnt}>({doneCnt}/{data.req.length})</span>
          </div>
          {games.map((g) => (
            <div key={`peer-${g.bookingId}`} className={`${s.card} ${g.subjects.every((x) => x.done) ? s.cardDone : ""}`}>
              <div className={s.head}>
                <span className={s.time}>{g.time}</span>
                <span className={s.quest}>{g.quest}</span>
                {g.branch ? <span className={s.branch}>{g.branch}</span> : null}
              </div>
              {g.subjects.map((x) => (
                <div key={x.id ?? "any"} className={s.subj}>
                  <span className={s.name}>{x.name}</span>
                  {!g.open ? <span className={s.wait}>откроется в {opensAt(g.time)}</span>
                    : x.done ? <button type="button" className={s.done} title="Тап — переписать" onClick={() => setSheet(g.bookingId)}>сдано ✓</button>
                    : <button type="button" className={s.write} onClick={() => setSheet(g.bookingId)}>Написать</button>}
                </div>
              ))}
              <div className={s.hint}>{g.learning ? "учебная игра · " : ""}о ком написать — попросил ОО</div>
            </div>
          ))}
        </>
      ) : null}
      <PeerOnlySheet game={games.find((g) => g.bookingId === sheet) ?? null} onClose={close} onDone={close} />
    </>
  );
}
